import { Box, Stack, Typography, styled, type SxProps, type Theme } from "~/common/mui";
import { ControlRenderer, type ConfiguratorControlChange } from "../controls";
import type { GenericSubstepViewModel } from "./substepViewModel";

export interface GenericSubstepProps {
  viewModel: GenericSubstepViewModel;
  onControlChange: (change: ConfiguratorControlChange) => void;
  className?: string;
  sx?: SxProps<Theme>;
}

const Controls = styled(Box, {
  shouldForwardProp: (prop) => prop !== "layoutType" && prop !== "columns",
})<{ layoutType: "stack" | "grid" | "flow"; columns: number }>(({ layoutType, columns, theme }) => ({
  display: layoutType === "flow" ? "flex" : "grid",
  flexWrap: layoutType === "flow" ? "wrap" : undefined,
  width: "100%",
  gap: layoutType === "flow" ? theme.spacing(1) : theme.spacing(2),
  containerType: "inline-size",
  gridTemplateColumns:
    layoutType === "grid"
      ? `repeat(${columns}, minmax(0, 1fr))`
      : layoutType === "stack"
      ? "minmax(0, 1fr)"
      : undefined,
  [`@container (max-width: ${theme.spacing(55)})`]: {
    gridTemplateColumns: layoutType === "flow" ? undefined : "minmax(0, 1fr)",
  },
}));

export const GenericSubstep = ({ viewModel, onControlChange, className, sx }: GenericSubstepProps) => {
  const layout = viewModel.controlsLayout ?? { type: "stack" as const };
  const columns = layout.type === "grid" ? layout.columns : 1;

  return (
    <Stack gap={1} className={className} sx={sx}>
      {viewModel.title ? <Typography variant="bodyLarge">{viewModel.title}</Typography> : null}
      {viewModel.description ? (
        <Typography variant="bodyMedium" color="textSecondary">
          {viewModel.description}
        </Typography>
      ) : null}
      <Controls layoutType={layout.type} columns={columns}>
        {viewModel.controls.map((control) => (
          <ControlRenderer key={control.controlIndex} viewModel={control} onChange={onControlChange} />
        ))}
      </Controls>
    </Stack>
  );
};
